import { formatAmounts } from '@aytm/app/stats/lib/util/helpers'
import { linkRowFromPoint } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/pointHalo'
import {
  buildBaseConfig,
  legendWidthFor
} from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/baseConfig'
import { createTooltipFormatter } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/tooltipFormatter'
import { sortData } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/sortData'
import { getErrData } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/errData'
import {
  calcMinMax,
  calcTickPositions,
  resolveZoomExtremes
} from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/calcMinMax'
import {
  removeLogicAndTruncate,
  transformText
} from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/textUtils'
import { CHART_HEIGHT } from '@aytm/app/stats/page/question-views/components/lib/constants'

const CHART_MARGIN = [84, 37, 30, 85]

// Two points sharing a place on the scale are drawn as one stacked column -- a trait split puts a
// segment in it per trait value. A figure per segment then labels parts of a column rather than the
// column, stacked into each other, and says nothing the segment's own tooltip does not.
const isStacked = (series = []) => {
  const places = new Set()

  for (const s of series) {
    for (const point of s.data || []) {
      if (places.has(point.x)) return true
      places.add(point.x)
    }
  }

  return false
}

// A MaxDiff chart plots its two probability measures as counts, but they are percentages everywhere
// else they are read, so they are printed as any other percentage.
const isMdProbability = props =>
  'md' === props.qType && ('varyingProbability' === props.viewMode || 'probability' === props.viewMode)

// the precision the tooltip uses: the chart's own format first, then the report's, then one decimal
const resolveLabelDecimals = props =>
  Object.hasOwn(props.format || {}, 'percentDecimals')
    ? props.format.percentDecimals
    : (props.reportPercentDecimals ?? 1)

export const buildColumnBarIngot = (props = {}) => {
  return buildBaseConfig({
    chart: {
      animation: false,
      borderWidth: 0,
      ignoreHiddenSeries: true,
      margin: [...CHART_MARGIN]
    },
    plotOptions: {
      series: {
        animation: false,
        borderRadius: {
          radius: 10
        },
        borderWidth: 1,
        shadow: false,
        stacking: 'normal',
        states: {
          hover: {
            brightness: 0.05
          },
          inactive: {
            opacity: 1
          }
        }
      }
    },
    tooltip: {
      formatter: createTooltipFormatter({
        format: props.format,
        qType: props.qType,
        viewMode: props.viewMode,
        answerType: props.answerType,
        ignorePercents: props.ignorePercents,
        axisType: props.axisType,
        confidenceLevel: props.confidenceLevel,
        reportPercentDecimals: props.reportPercentDecimals
      })
    },
    yAxis: {
      min: 0
    }
  })
}

const calcGeneralData = (data, props) => {
  // the reader's percentage precision, handed to every chart from the container
  const percentDecimals = props.reportPercentDecimals

  const { width = 658, qType, aType, rType, format = {}, answerType = 'amount', chartMode } = props
  const { ignoreAmount, ignorePercents, plotLines: _plotLines, unitLabel: modelUnitLabel } = props
  const ignoreRefs = props.ignoreRefs || props.answerLabels === 'hide'

  const answerText =
    rType == null || rType === 'cases' ? (answerType === 'amount' ? 'respondent' : answerType) : 'response'

  const answer = props.answer || {}
  let answerVariants = props.answerVariants?.slice() || []
  const answerVariantsPositions = answerVariants.filter(av => av != null).map(av => av.position)

  answerVariants = sortData(answer, answerVariants, props.sortOrder, { answerType })

  const percentsMode = !ignorePercents && chartMode.endsWith('percents')
  const mode = percentsMode ? 'percents' : answerType

  let minYValue = 0
  let maxYValue = 0

  const themeColors = props.colorTheme

  data.series = []
  const xAxisCategories = []
  let xIndex = 0

  const columns = []
  for (let avi = 0; avi < answerVariants.length; avi++) {
    const av = answerVariants[avi]
    if (av == null || answer == null) continue

    const avPosition = av.position || 0
    const avColorIndex = answerVariantsPositions.indexOf(avPosition)
    const obj = {
      index: avi,
      colorIndex: avColorIndex,
      k: `a${avPosition}`,
      p: avPosition,
      c: themeColors[avColorIndex % themeColors.length]
    }
    const answerData = answer[obj.k]
    obj.v = answerData != null ? answer[obj.k][answerType] : 0
    columns.push(obj)
  }

  const chartLegendMode =
    chartMode !== 'brespondents' && chartMode !== 'bpercents' && columns.length > 12 ? 'short' : props.chartLegendMode

  for (const el of columns) {
    const av = answerVariants[el.index]
    const avi = el.index
    const avText = av.text
    const answerData = answer[el.k] || {}
    const avPosition = av.position

    const as = Object.assign({ [answerType]: 0, percents: 0 }, answerData || {})
    const amount = formatAmounts(as[answerType])

    const { err, minErrValue, maxErrValue } = getErrData(as, mode, {
      ignoreAmount,
      ignorePercents,
      rType,
      answerType,
      confidenceLevels: props.confidenceLevels
    })

    const percents =
      rType == null || rType === 'cases' ? as.percents : (as.respPercents != null ? as.respPercents : as.percents) || 0
    const prefixText = `${aType || 'A'}${avPosition != null ? avPosition : avi + 1}`
    const yIndex = ignoreAmount || percentsMode ? percents : parseFloat(as[answerType])

    let desc = ''

    if (qType === 'md') {
      const probability = isMdProbability(props)
      const shown = probability ? Number(as[answerType]).toFixed(resolveLabelDecimals(props)) : amount
      desc = !ignoreAmount ? `<div><b>${shown}${probability ? '%' : ''}</b>{err}</div>` : ''
    } else {
      desc = !ignorePercents
        ? `<div><b>${parseFloat(percents).toFixed(percentDecimals ?? 2)}</b>%{err}</div>`
        : ignoreAmount
          ? '<div>{err}</div>'
          : ''
      if (!ignoreAmount) {
        const unitLabel = as.unitLabel !== undefined ? as.unitLabel : modelUnitLabel

        let respInfo = `<b>${
          as.unitTemplate != null && as.unitTemplate.length ? as.unitTemplate.replace(/{{unit}}/gi, amount) : amount
        }</b>`
        respInfo +=
          unitLabel !== undefined
            ? unitLabel.length
              ? ' ' + unitLabel
              : ''
            : answerType === 'amount'
              ? ' ' + answerText + (Number(amount) !== 1 ? 's' : '')
              : ''

        desc += ignorePercents ? `<div>${respInfo}{err}</div>` : `<div>${respInfo}</div>`
      }
    }

    if ((qType === 'mx' || qType === 'mp') && as.sd !== undefined) {
      desc += `<div><b>${formatAmounts(as.sd)}</b> SD</div>`
    }
    if ((qType === 'mx' || qType === 'mp') && as.sq !== undefined && as.sq > 0) {
      desc += `<div><b>${formatAmounts(as.sq)}</b> SQ n</div>`
    }

    desc += `<div class='highcharts-tooltip-text'><b>${ignoreRefs ? '' : prefixText + ':'}</b> ${removeLogicAndTruncate(
      avText,
      100
    )}</div>`

    const pointData = {
      id: `a${avPosition}`,
      name: desc,
      x: xIndex,
      y: yIndex,
      errData: {
        i: el.index,
        err,
        [answerType]: parseFloat(as[answerType]),
        answer: as,
        total: as.base !== undefined ? as.base : answer.base !== undefined ? answer.base : as.total || answer.total
      }
    }

    if (Object.hasOwn(as, 'sd')) {
      pointData.errData.sd = as.sd
      pointData.errData.mu = as.mu
    }

    if (format.useSigTest === true) {
      pointData.errData.sigtest = as.sigtest
    }

    // Matrix views sort batches; sigtest references original indices
    if (as.sigtestIndex != null) {
      pointData.errData.i = as.sigtestIndex
    }

    const legendText = ignoreRefs
      ? removeLogicAndTruncate(`${avText}`, 27)
      : chartLegendMode === 'short'
        ? prefixText
        : removeLogicAndTruncate(`${prefixText}: ${avText}`, 27)

    data.series.push({
      name: legendText,
      color: el.c,
      colorIndex: el.colorIndex,
      data: [pointData]
    })

    minYValue = Math.min(minYValue, yIndex - (minErrValue || 0))
    maxYValue = Math.max(maxYValue, yIndex + (maxErrValue || 0))

    if (chartMode === 'brespondents' || chartMode === 'bpercents') {
      const _textSize = 12
      xAxisCategories.push(chartLegendMode === 'short' && !ignoreRefs ? prefixText : transformText(avText, _textSize))
    } else {
      if (answerVariants.length < 20) {
        const _textSize = (80 * width) / (746 * answerVariants.length)
        xAxisCategories.push(chartLegendMode === 'short' && !ignoreRefs ? prefixText : transformText(avText, _textSize))
      } else {
        xAxisCategories.push('')
      }
    }

    xIndex += 1
  }

  const computedMinY = minYValue < 0 ? -Math.ceil(Math.abs(minYValue)) : 0
  const computedMaxY =
    maxYValue <= 0 && minYValue >= 0 ? 100 : maxYValue <= 0 ? Math.ceil(Math.abs(minYValue)) : Math.ceil(maxYValue)

  data.yAxis.min = computedMinY
  data.yAxis.max = computedMaxY

  const mmResult = calcMinMax(data, percentsMode, {
    answer,
    rType,
    yAxisLimits: props.yAxisLimits,
    zoomType: props.zoomType,
    // best-fit extremes (coffee @minYValue/@maxYValue) — calcMinMax clamps them to yAxisLimits and returns back
    minYValue: computedMinY,
    maxYValue: computedMaxY
  })

  // The floor only keeps a near-zero column visible; the label still has to read the column's own
  // value, so the value it replaced travels with the point.
  for (const s of data.series) {
    const { y } = s.data[0]
    if (y < 0.005 && y >= 0) s.data[0].y = 0.005
    if (y > -0.005 && y < 0) s.data[0].y = -0.005
    if (s.data[0].y !== y) s.data[0].labelY = y
  }

  data.xAxis.categories = xAxisCategories
  data.yAxis.title.text = props.yAxisTitle || (percentsMode ? 'percents' : `${answerText}s`)

  // baked-in zoom: coffee applied zoomLevel imperatively (calcExtremes load/setExtremes),
  // here extremes go straight into options — gated on zoomType only, like coffee calcMinMax
  let zoom
  if (props.zoomType) {
    const best = { min: mmResult.minYValue, max: mmResult.maxYValue }
    const full = { min: data.yAxis.min, max: data.yAxis.max }

    const ext = resolveZoomExtremes(props.zoomLevel || props.globalZoomLevel, best, full)

    data.yAxis.min = ext.min
    data.yAxis.max = ext.max
    data.yAxis.tickPositions = calcTickPositions(ext.min, ext.max)

    // both seeded from best.min — quirk kept 1:1 with coffee renderZoomLevel
    let seriesMin = best.min
    let seriesMax = best.min
    for (const s of data.series) {
      for (const p of s.data) {
        seriesMin = Math.min(seriesMin, p.y)
        seriesMax = Math.max(seriesMax, p.y)
      }
    }
    if (seriesMin < 0 && seriesMax < 0) seriesMax = Math.abs(seriesMin)

    data.chart.events = {
      ...data.chart.events,
      selection(event) {
        event.preventDefault?.()

        const min = props.ignorePercents ? Number(event.yAxis[0].min.toFixed(2)) : Math.floor(event.yAxis[0].min)
        const max = props.ignorePercents ? Number(event.yAxis[0].max.toFixed(2)) : Math.ceil(event.yAxis[0].max)

        let level = min === best.min && max === best.max && 'min'
        level = level || (min === full.min && max === full.max && 'max')
        level = level || [min, max]

        props.onZoomChange?.(props.questionKey, level)
      }
    }
    data.chart.zooming = {
      type: 'y',
      events: {
        render() {
          this.resetZoomButton?.hide()
        }
      }
    }

    zoom = {
      dataMin: best.min,
      dataMax: best.max,
      axisMin: full.min,
      axisMax: full.max,
      currentMin: ext.min,
      currentMax: ext.max,
      seriesMin,
      seriesMax
    }
  }

  return { zoom }
}

const calcTraitData = (data, props) => {
  // the reader's percentage precision, handed to every chart from the container
  const percentDecimals = props.reportPercentDecimals

  const {
    width = 658,
    aType,
    rType,
    answerType = 'amount',
    chartMode,
    traitName,
    trait,
    ignoreAmount,
    ignorePercents,
    answer = {},
    sortOrder,
    colorTheme,
    traitValues = []
  } = props
  const ignoreRefs = props.ignoreRefs || props.answerLabels === 'hide'

  const answerText =
    rType == null || rType === 'cases' ? (answerType === 'amount' ? 'respondent' : answerType) : 'response'

  const answerVariants = sortData(answer, props.answerVariants || [], sortOrder, { answerType })

  const chartLegendMode =
    !['brespondents', 'bpercents'].includes(chartMode) && answerVariants.length > 12 ? 'short' : props.chartLegendMode

  const percentsMode = !ignorePercents && chartMode.endsWith('percents')

  let minYValue = 0
  let maxYValue = percentsMode ? 100 : 0

  data.series = []

  const xAxisCategories = []
  const yValues = Array.from({ length: answerVariants.length }, () => 0)

  for (let traitIndex = 0; traitIndex < traitValues.length; traitIndex++) {
    const traitValue = traitValues[traitIndex]
    const s = {
      name: traitValue.name,
      color: colorTheme[traitIndex % colorTheme.length],
      colorIndex: traitIndex,
      data: []
    }
    let xIndex = 0

    for (let avi = 0; avi < answerVariants.length; avi++) {
      const av = answerVariants[avi]
      if (!av) continue

      const avText = av.text
      const avPosition = av.position

      const as = answer[`a${avPosition}`] || {}
      const traitId = Object.keys(as[traitName] || {}).find(
        v => String(v).toLowerCase() === String(traitValue.id).toLowerCase()
      )

      const ats = Object.assign(
        { [answerType]: 0, percents: 0 },
        answer[`a${avPosition}`]?.[traitName]?.[traitId] || {}
      )

      const percents =
        rType == null || rType === 'cases'
          ? ats.percents
          : (ats.respPercents != null ? ats.respPercents : ats.percents) || 0
      const prefixText = `${aType || 'A'}${avPosition != null ? avPosition : avi + 1}`
      const yIndex = ignoreAmount || percentsMode ? percents : parseFloat(ats[answerType])

      let desc = `<div>${trait.title || trait.text || traitName}: <b>${traitValue.name}</b></div>`
      desc += '<div>'
      desc += !ignorePercents ? `<b>${parseFloat(percents).toFixed(percentDecimals ?? 1)}</b>%` : ''
      if (!ignoreAmount) {
        const amount = formatAmounts(ats[answerType])
        const unitLabel = as.unitLabel !== undefined ? as.unitLabel : props.unitLabel

        let respInfo = `<strong>${
          ats.unitTemplate != null && ats.unitTemplate.length ? ats.unitTemplate.replace(/{{unit}}/gi, amount) : amount
        }</strong>`
        respInfo +=
          unitLabel !== undefined
            ? unitLabel.length
              ? ' ' + unitLabel
              : ''
            : answerType === 'amount'
              ? ' ' + answerText + (Number(amount) !== 1 ? 's' : '')
              : ''

        desc += ` ${respInfo}`
      }
      desc += '</div>'
      desc += `<div class='highcharts-tooltip-text'><b>${
        ignoreRefs ? '' : prefixText + ':'
      }</b> ${removeLogicAndTruncate(avText, 100)}</div>`

      s.data.push({
        // the answer and the trait value together, because the split draws one segment per pair --
        // the trait row below numbers the same values in the same order the trait was read in
        id: `a${avPosition}t${traitIndex + 1}`,
        name: desc,
        x: xIndex,
        y: yIndex
      })

      if (traitIndex === 0) {
        if (chartMode === 'brespondents' || chartMode === 'bpercents') {
          const _textSize = 12
          xAxisCategories.push(
            chartLegendMode === 'short' && !ignoreRefs ? prefixText : transformText(avText, _textSize)
          )
        } else {
          if (answerVariants.length < 20) {
            const _textSize = (80 * width) / (746 * answerVariants.length)
            xAxisCategories.push(
              chartLegendMode === 'short' && !ignoreRefs ? prefixText : transformText(avText, _textSize)
            )
          } else {
            xAxisCategories.push('')
          }
        }
      }

      yValues[avi] += yIndex
      xIndex += 1
    }

    data.series.unshift(s)
  }

  minYValue = Math.min(...yValues)
  maxYValue = Math.max(...yValues)

  if (Array.isArray(props.yAxisLimits) && props.yAxisLimits.length === 2) {
    data.yAxis.min = props.yAxisLimits[0]
    data.yAxis.max = props.yAxisLimits[1]
  } else {
    const computedMinY = minYValue < 0 ? -Math.ceil(Math.abs(minYValue)) : 0
    const computedMaxY =
      maxYValue <= 0 && minYValue >= 0 ? 100 : maxYValue <= 0 ? Math.ceil(Math.abs(minYValue)) : Math.ceil(maxYValue)

    data.yAxis.min = computedMinY
    data.yAxis.max = computedMaxY
  }

  data.yAxis.tickPositions = calcTickPositions(data.yAxis.min, data.yAxis.max)

  data.xAxis.categories = xAxisCategories
  data.yAxis.title.text = props.yAxisTitle || (percentsMode ? 'percents' : `${answerText}s`)

  data.legend.reversed = true
}

export const buildColumnBarOptions = props => {
  const data = buildColumnBarIngot(props)

  const { trait, traitName, questionKey, ignoreTraits } = props

  let zoom
  if (trait && (questionKey === undefined || questionKey !== traitName) && !ignoreTraits) {
    calcTraitData(data, props) // trait breakdown never zooms (coffee parity)
  } else {
    ;({ zoom } = calcGeneralData(data, props))
  }

  // Hovering a column hands it two jobs: its own label steps aside for the tooltip about to cover
  // the same figure, and its row in the list below tints. Set on plotOptions, because that is where
  // the margin-of-error wiring looks -- it chains whatever it finds there ahead of its own handlers,
  // and a per-series copy would win the merge and silently drop the confidence overlay.
  data.plotOptions.series.point = {
    ...data.plotOptions.series.point,
    events: {
      ...data.plotOptions.series.point?.events,
      mouseOut() {
        this.dataLabel?.show()
        linkRowFromPoint(this.series?.chart, null)
      },
      mouseOver() {
        this.dataLabel?.hide()
        linkRowFromPoint(this.series?.chart, this.id)
      }
    }
  }

  // The label states what the axis plots: the percentage on a percents chart, at the precision the
  // tooltip uses, and the count otherwise; a point that has no count of its own falls back to the
  // value it plots. Set here and not in the ingot, because the stacked builders take the ingot
  // directly and stay unlabelled.
  const labelAnswerType = props.answerType || 'amount'
  const labelsPercents =
    isMdProbability(props) || (!props.ignorePercents && String(props.chartMode || '').endsWith('percents'))
  const labelDecimals = resolveLabelDecimals(props)
  data.plotOptions.series.dataLabels = {
    ...data.plotOptions.series.dataLabels,
    crop: false,
    enabled: !isStacked(data.series),
    formatter() {
      const plotted = this.point?.labelY ?? this.y
      if (labelsPercents) {
        return null == plotted || Number.isNaN(Number(plotted)) ? '' : `${Number(plotted).toFixed(labelDecimals)}%`
      }
      const counted = props.ignoreAmount ? null : this.point?.errData?.[labelAnswerType]
      const raw = null == counted ? plotted : counted
      return null == raw || Number.isNaN(Number(raw)) ? '' : formatAmounts(raw)
    },
    // the ingot stacks every series, and a stacked column keeps its labels inside the bar by
    // default -- these belong clear of the shape, above a column and right of a bar
    inside: false,
    overflow: 'allow',
    style: {
      ...data.plotOptions.series.dataLabels?.style,
      // the default contrast outline haloes the figure against the fill it no longer sits on
      textOutline: 'none'
    },
    y: -4
  }

  const plotLines = props.plotLines || []
  if (plotLines && Array.isArray(plotLines)) {
    const _plotLines = plotLines.map(v => ({
      color: '#333333',
      width: 1,
      value: v,
      zIndex: 3
    }))
    if (data.yAxis.min < 0) {
      _plotLines.push({
        color: '#999999',
        width: 1,
        value: 0,
        zIndex: 3
      })
    }
    data.yAxis.plotLines = _plotLines
  }

  if (data.chart) {
    data.chart.width = props.width || 658
    data.chart.height = props.height || CHART_HEIGHT
    data.legend.width = legendWidthFor(data.chart.width)
  }

  return { options: data, zoom }
}
